import { useCallback, useEffect, useState } from 'react';
import type { McpServerConfig, McpServerStatus } from '../../../types/mcpServerTypes';

type AiToolbarStatus = {
  isLoading: boolean;
  mcpEnabled: boolean;
  mcpRunning: boolean;
  mcpPort: number | undefined;
  mcpEndpoint: string | undefined;
  error: string | null;
};

const initialStatus: AiToolbarStatus = {
  isLoading: true,
  mcpEnabled: false,
  mcpRunning: false,
  mcpPort: undefined,
  mcpEndpoint: undefined,
  error: null,
};

/**
 * Hook to get MCP server status for the AI toolbar button.
 * Polls status periodically to keep the indicator up-to-date.
 */
export function useAiToolbarStatus() {
  const [status, setStatus] = useState<AiToolbarStatus>(initialStatus);

  const refreshStatus = useCallback(async () => {
    const api = window.electronAPI?.mcpServer;
    if (!api) {
      setStatus({
        isLoading: false,
        mcpEnabled: false,
        mcpRunning: false,
        mcpPort: undefined,
        mcpEndpoint: undefined,
        error: null, // Not an error - just not available in browser mode
      });
      return;
    }

    try {
      // Use getStatusLight to avoid triggering keychain access on every status check
      const result = await api.getStatusLight();
      if (result.success && result.config && result.status) {
        const config = result.config as McpServerConfig;
        const serverStatus = result.status as McpServerStatus;
        setStatus({
          isLoading: false,
          mcpEnabled: config.enabled,
          mcpRunning: serverStatus.running,
          mcpPort: serverStatus.port ?? undefined,
          mcpEndpoint: serverStatus.running && serverStatus.port
            ? `http://127.0.0.1:${serverStatus.port}/mcp`
            : undefined,
          error: null,
        });
      } else {
        setStatus({
          isLoading: false,
          mcpEnabled: false,
          mcpRunning: false,
          mcpPort: undefined,
          mcpEndpoint: undefined,
          error: result.error ?? null,
        });
      }
    } catch (err) {
      setStatus({
        isLoading: false,
        mcpEnabled: false,
        mcpRunning: false,
        mcpPort: undefined,
        mcpEndpoint: undefined,
        error: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  }, []);

  // Initial load
  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  // Poll status every 8 seconds to keep indicator updated
  useEffect(() => {
    const interval = setInterval(refreshStatus, 8000);
    return () => clearInterval(interval);
  }, [refreshStatus]);

  return { ...status, refreshStatus };
}
